import React from 'react';
import { DatabaseOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { DatabaseConfig } from '../../../types/database.type';
import { databaseEngines } from '../../../constants/databaseEngine';
import { PricingPlan } from '../../../services/pricing.service';

interface Step4Props {
  config: DatabaseConfig;
  plans?: PricingPlan[];
}

const Step4Review: React.FC<Step4Props> = ({ config, plans = [] }) => {
  const selectedEngine = databaseEngines.find((e) => e.id === config.engine);
  const isMongoDb = config.engine === 'mongodb';

  const selectedPlan = plans.find((p) => p.planCode === config.planCode);

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount / 100);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <DatabaseOutlined className="text-xl" />
          Configuration Summary
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-600">Database Name</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-600">Engine</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedEngine?.name} {config.version}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-600">Instance Type</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.instanceType}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-600">Storage</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.storage} GB</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-600">Port</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.port}</span>
          </div>
          {!isMongoDb && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600">Automated Backups</span>
              <span className="font-medium text-gray-900 dark:text-white">
                Enabled (7-day retention)
              </span>
            </div>
          )}
          {isMongoDb && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600">Deployment</span>
              <span className="font-medium text-gray-900 dark:text-white">Dedicated EC2 Instance</span>
            </div>
          )}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Public Access</span>
            <span
              className={`font-medium ${
                config.publicAccess ? 'text-orange-600' : 'text-green-600'
              }`}
            >
              {config.publicAccess ? (
                <>
                  <UnlockOutlined /> Public
                </>
              ) : (
                <>
                  <LockOutlined /> Private
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-900 dark:text-white">Costo mensual</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {selectedPlan?.priceAmount != null ? formatPrice(selectedPlan.priceAmount) : '—'}
          </p>
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
          {selectedPlan && <p>• Plan: {selectedPlan.planName || selectedPlan.planCode}</p>}
          <p>• Incluye {config.storage} GB SSD{!isMongoDb ? ' y backups automáticos 7 días' : ''}</p>
          <p>• Precio incluye IVA</p>
        </div>
      </div>

      {config.publicAccess && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-orange-900 mb-1">⚠️ Security Warning</p>
          <p className="text-xs text-orange-800">
            You've enabled public access. Make sure to configure security groups and use strong
            authentication to protect your database.
          </p>
        </div>
      )}

      {isMongoDb && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm font-semibold text-green-900 dark:text-green-200 mb-2">MongoDB Connection</p>
          <code className="block text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded px-3 py-2 mb-2">
            mongodb://kubiyadmin:&lt;password&gt;@&lt;host&gt;:{config.port}/admin
          </code>
          <p className="text-xs text-green-800 dark:text-green-300">
            MongoDB runs on a dedicated EC2 instance. Backups can be configured post-deployment via mongodump or EBS snapshots.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step4Review;